import React from "react";

import useEmblaCarousel from "embla-carousel-react";

const TeamDetailAboutTab = ({
  sportsDbTeam,
  apiSportsTeam,
  teamColors,
  aboutExpanded,
  setAboutExpanded,
  equipment,
  recentEvents,
  honours,
  loading,
  navigate,
}) => {
  const [trophyEmblaRef] = useEmblaCarousel({
    dragFree: true,
    containScroll: false,
    align: "start",
  });

  if (loading) {
    return (
      <div className="space-y-10 animate-pulse">
        <div className="h-40 bg-white/[0.03] rounded-xl border border-white/[0.06]" />
        <div className="h-32 bg-white/[0.03] rounded-xl border border-white/[0.06]" />
        <div className="h-48 bg-white/[0.03] rounded-xl border border-white/[0.06]" />
      </div>
    );
  }

  if (!sportsDbTeam && !apiSportsTeam) {
    return (
      <div className="text-center py-20">
        <i className="fas fa-info-circle text-5xl text-white/10 mb-4" />
        <p className="text-white/40 text-lg">Team information not available</p>
      </div>
    );
  }

  const description = sportsDbTeam?.strDescriptionEN;
  const shortDesc = description
    ? `${description.split(". ").slice(0, 3).join(". ")}.`
    : null;

  const venue = apiSportsTeam?.venue || null;
  const nickname = sportsDbTeam?.strKeywords || null;
  const altName = sportsDbTeam?.strTeamAlternate || null;
  const shortName = sportsDbTeam?.strTeamShort || null;

  // Collect all fanart images
  const fanartImages = [
    sportsDbTeam?.strFanart1,
    sportsDbTeam?.strFanart2,
    sportsDbTeam?.strFanart3,
    sportsDbTeam?.strFanart4,
  ].filter(Boolean);

  // Get current season kits (latest season with equipment)
  const kitsBySeason = {};

  (equipment || []).forEach((kit) => {
    if (!kitsBySeason[kit.strSeason]) kitsBySeason[kit.strSeason] = [];
    kitsBySeason[kit.strSeason].push(kit);
  });
  const seasons = Object.keys(kitsBySeason).sort().reverse();
  const currentSeasonKits = seasons.length > 0 ? kitsBySeason[seasons[0]] : [];
  const currentSeason = seasons[0] || null;

  // Social links helper
  const socialLink = (url) => {
    if (!url) return null;

    return `https://${url.replace(/^https?:\/\//, "")}`;
  };

  return (
    <div className="space-y-10">
      {/* ─── Team Identity Card ──────────────────────────────── */}
      <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden">
        {/* Banner image */}
        {sportsDbTeam?.strBanner && (
          <img
            alt=""
            className="w-full h-40 object-cover"
            src={sportsDbTeam.strBanner}
          />
        )}
        <div className="p-6">
          <div className="flex items-start gap-6">
            {/* Badge + Logo */}
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              {(sportsDbTeam?.strBadge || sportsDbTeam?.strLogo) && (
                <img
                  alt=""
                  className="w-20 h-20 object-contain"
                  src={sportsDbTeam.strBadge || sportsDbTeam.strLogo}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-white">
                {sportsDbTeam?.strTeam || apiSportsTeam?.team?.name}
              </h3>
              {(altName || shortName) && (
                <p className="text-white/40 text-sm mt-0.5">
                  {[altName, shortName && `(${shortName})`]
                    .filter(Boolean)
                    .join(" ")}
                </p>
              )}
              {nickname && (
                <p className="text-white/50 text-sm mt-1 italic">
                  &ldquo;{nickname}&rdquo;
                </p>
              )}
              {/* Quick facts row */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm text-white/40">
                {sportsDbTeam?.intFormedYear && (
                  <span>
                    <i className="fas fa-calendar-alt text-white/20 mr-1.5" />
                    Founded {sportsDbTeam.intFormedYear}
                  </span>
                )}
                {sportsDbTeam?.strCountry && (
                  <span>
                    <i className="fas fa-flag text-white/20 mr-1.5" />
                    {sportsDbTeam.strCountry}
                  </span>
                )}
                {sportsDbTeam?.strSport && (
                  <span>
                    <i className="fas fa-futbol text-white/20 mr-1.5" />
                    {sportsDbTeam.strSport}
                  </span>
                )}
                {sportsDbTeam?.strLeague && (
                  <span>
                    <i className="fas fa-trophy text-white/20 mr-1.5" />
                    {sportsDbTeam.strLeague}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Honours Rail (NEW) ───────────────────────────────── */}
      {honours && honours.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            Honours
            <span className="text-amber-400/60 text-sm font-normal ml-2">
              ({honours.length})
            </span>
          </h3>
          <div data-carousel className="overflow-visible" ref={trophyEmblaRef}>
            <div className="flex gap-3">
              {[...honours]
                .sort((a, b) =>
                  (b.strSeason || "").localeCompare(a.strSeason || "")
                )
                .map((trophy, i) => (
                  <div
                    className="flex-shrink-0 w-36 bg-amber-500/[0.04] rounded-xl border border-amber-500/[0.15] border-t-2 border-t-amber-500/60 p-4 text-center"
                    key={trophy.idHonour || i}
                  >
                    {trophy.strThumbnail && (
                      <img
                        alt=""
                        className="w-12 h-12 object-contain mx-auto mb-2"
                        src={trophy.strThumbnail}
                      />
                    )}
                    <p className="text-white text-xs font-semibold leading-tight">
                      {trophy.strHonour}
                    </p>
                    <p className="text-amber-400/60 text-[10px] mt-1">
                      {trophy.strSeason}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── Description ─────────────────────────────────────── */}
      {description && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">About</h3>
          <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] p-6">
            <p className="text-white/60 text-sm leading-relaxed whitespace-pre-line">
              {aboutExpanded ? description : shortDesc}
            </p>
            {description.split(". ").length > 3 && (
              <button
                className="text-blue-400 text-sm mt-3 hover:text-blue-300 transition-colors"
                onClick={() => setAboutExpanded((v) => !v)}
              >
                {aboutExpanded ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── Competitions + Venue (side by side) ────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Competitions */}
        {(sportsDbTeam?.strLeague2 || sportsDbTeam?.strLeague3) && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">
              Competitions
            </h3>
            <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.04]">
              {[
                sportsDbTeam?.strLeague,
                sportsDbTeam?.strLeague2,
                sportsDbTeam?.strLeague3,
                sportsDbTeam?.strLeague4,
                sportsDbTeam?.strLeague5,
                sportsDbTeam?.strLeague6,
                sportsDbTeam?.strLeague7,
              ]
                .filter(Boolean)
                .map((league) => (
                  <button
                    className="w-full flex items-center gap-3 p-3 hover:bg-white/[0.03] transition-colors text-left focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:ring-inset"
                    key={league}
                    tabIndex={0}
                    onClick={() =>
                      navigate(
                        `/web-player/sports/league/${encodeURIComponent(league)}`
                      )
                    }
                  >
                    <i className="fas fa-trophy text-white/20 text-sm" />
                    <span className="text-white/70 text-sm">{league}</span>
                    <i className="fas fa-chevron-right text-white/15 text-xs ml-auto" />
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Venue */}
        {(sportsDbTeam?.strStadium || venue) && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Venue</h3>
            <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden">
              {(venue?.image || fanartImages[1]) && (
                <img
                  alt={sportsDbTeam?.strStadium || venue?.name}
                  className="w-full h-44 object-cover"
                  src={venue?.image || fanartImages[1]}
                />
              )}
              <div className="p-5">
                <p className="text-white font-semibold text-lg">
                  {sportsDbTeam?.strStadium || venue?.name}
                </p>
                <p className="text-white/40 text-sm mt-1">
                  {sportsDbTeam?.strLocation || venue?.city || ""}
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm text-white/40">
                  {(sportsDbTeam?.intStadiumCapacity || venue?.capacity) && (
                    <span>
                      <i className="fas fa-users text-white/20 mr-1.5" />
                      Capacity:{" "}
                      {Number(
                        sportsDbTeam?.intStadiumCapacity || venue?.capacity
                      ).toLocaleString()}
                    </span>
                  )}
                  {venue?.surface && (
                    <span>
                      <i className="fas fa-leaf text-white/20 mr-1.5" />
                      Surface: {venue.surface}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Kits + Club Colors (side by side) ───────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Kits */}
        {currentSeasonKits.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">Kits</h3>
            <p className="text-white/30 text-xs mb-4">{currentSeason}</p>
            <div
              className="flex gap-4 overflow-x-auto pb-2"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255,255,255,0.1) transparent",
              }}
            >
              {currentSeasonKits.map((kit) => (
                <div
                  className="flex-shrink-0 bg-white/[0.03] rounded-xl border border-white/[0.06] p-4 text-center"
                  key={kit.idEquipment}
                >
                  <img
                    alt={`${kit.strType} kit`}
                    className="w-28 h-auto mx-auto"
                    src={kit.strEquipment}
                  />
                  <p className="text-white/50 text-xs mt-2 font-medium">
                    {kit.strType === "1st"
                      ? "Home"
                      : kit.strType === "2nd"
                        ? "Away"
                        : kit.strType === "3rd"
                          ? "Third"
                          : kit.strType}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Club Colors */}
        {(teamColors.primary !== "#3B82F6" || teamColors.tertiary) && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">
              Club Colors
            </h3>
            <div className="flex gap-5">
              {[
                { color: teamColors.primary, label: "Primary" },
                { color: teamColors.secondary, label: "Secondary" },
                teamColors.tertiary
                  ? { color: teamColors.tertiary, label: "Tertiary" }
                  : null,
              ]
                .filter(Boolean)
                .map((c) => (
                  <div className="flex items-center gap-3" key={c.label}>
                    <div
                      className="w-10 h-10 rounded-lg border-2 border-white/10"
                      style={{ background: c.color }}
                    />
                    <div>
                      <span className="text-white/50 text-xs block">
                        {c.label}
                      </span>
                      <span className="text-white/25 text-[10px] font-mono block">
                        {c.color}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── Gallery ─────────────────────────────────────────── */}
      {fanartImages.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">Gallery</h3>
          <div className="grid grid-cols-2 gap-3">
            {fanartImages.map((url, i) => (
              <div
                className="rounded-xl overflow-hidden border border-white/[0.06]"
                key={i}
              >
                <img
                  alt=""
                  className="w-full h-40 object-cover hover:scale-105 transition-transform duration-300"
                  src={url}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Recent Results (TheSportsDB) ────────────────────── */}
      {recentEvents && recentEvents.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">
            Recent Results
          </h3>
          <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.04]">
            {recentEvents.slice(0, 10).map((evt) => {
              const homeWin =
                Number(evt.intHomeScore) > Number(evt.intAwayScore);
              const draw = evt.intHomeScore === evt.intAwayScore;
              const isHome = evt.idHomeTeam === sportsDbTeam?.idTeam;
              const won = isHome ? homeWin : !homeWin && !draw;

              return (
                <div
                  className="flex items-center gap-4 p-3 px-5"
                  key={evt.idEvent}
                >
                  <div className="w-20 text-xs text-white/30 flex-shrink-0">
                    {evt.dateEvent}
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {evt.strHomeTeamBadge && (
                      <img
                        alt=""
                        className="w-5 h-5 object-contain flex-shrink-0"
                        src={evt.strHomeTeamBadge}
                      />
                    )}
                    <span
                      className={`text-sm truncate ${
                        isHome ? "text-white font-semibold" : "text-white/60"
                      }`}
                    >
                      {evt.strHomeTeam}
                    </span>
                    <span className="text-white font-bold text-sm mx-1">
                      {evt.intHomeScore} - {evt.intAwayScore}
                    </span>
                    {evt.strAwayTeamBadge && (
                      <img
                        alt=""
                        className="w-5 h-5 object-contain flex-shrink-0"
                        src={evt.strAwayTeamBadge}
                      />
                    )}
                    <span
                      className={`text-sm truncate ${
                        !isHome ? "text-white font-semibold" : "text-white/60"
                      }`}
                    >
                      {evt.strAwayTeam}
                    </span>
                  </div>
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border flex-shrink-0 ${
                      won
                        ? "text-green-400 bg-green-500/15 border-green-500/30"
                        : draw
                          ? "text-yellow-400 bg-yellow-500/15 border-yellow-500/30"
                          : "text-red-400 bg-red-500/15 border-red-500/30"
                    }`}
                  >
                    {won ? "W" : draw ? "D" : "L"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Social Links ────────────────────────────────────── */}
      {(sportsDbTeam?.strWebsite ||
        sportsDbTeam?.strFacebook ||
        sportsDbTeam?.strTwitter ||
        sportsDbTeam?.strInstagram ||
        sportsDbTeam?.strYoutube) && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">Social</h3>
          <div className="flex flex-wrap gap-3">
            {[
              {
                url: sportsDbTeam.strWebsite,
                icon: "fas fa-globe",
                label: "Website",
                hoverColor: "hover:text-white/70",
              },
              {
                url: sportsDbTeam.strFacebook,
                icon: "fab fa-facebook-f",
                label: "Facebook",
                hoverColor: "hover:text-blue-400",
              },
              {
                url: sportsDbTeam.strTwitter,
                icon: "fab fa-twitter",
                label: "Twitter / X",
                hoverColor: "hover:text-sky-400",
              },
              {
                url: sportsDbTeam.strInstagram,
                icon: "fab fa-instagram",
                label: "Instagram",
                hoverColor: "hover:text-pink-400",
              },
              {
                url: sportsDbTeam.strYoutube,
                icon: "fab fa-youtube",
                label: "YouTube",
                hoverColor: "hover:text-red-400",
              },
            ]
              .filter((s) => s.url)
              .map((s) => (
                <a
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white/40 ${s.hoverColor} hover:bg-white/[0.08] transition-all text-sm`}
                  href={socialLink(s.url)}
                  key={s.label}
                  rel="noopener noreferrer"
                  target="_blank"
                  title={s.label}
                >
                  <i className={`${s.icon} text-sm`} />
                  <span className="text-xs">{s.label}</span>
                </a>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetailAboutTab;
